import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { 
  TrendingUp, 
  DollarSign, 
  Target, 
  Users, 
  Globe, 
  Leaf, 
  Building, 
  Calculator,
  BarChart3,
  CheckCircle2,
  Plus,
  Edit,
  Save,
  RefreshCw,
  Award,
  Zap,
  Heart,
  Shield,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const SROICalculator = ({ projectId, user }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [dashboardData, setDashboardData] = useState({});
  const [sroiData, setSroiData] = useState({});
  const [tocData, setTocData] = useState({});
  const [sdgsData, setSdgsData] = useState({});
  const [esgData, setEsgData] = useState({});
  
  // Editing states
  const [editingSroi, setEditingSroi] = useState(false);
  const [editingToc, setEditingToc] = useState(false);
  const [editingSdgs, setEditingSdgs] = useState(false);
  const [editingSdgGoal, setEditingSdgGoal] = useState(null); // Track which SDG goal is being edited
  const [editingEsg, setEditingEsg] = useState(false);
  const [editingEsgIndicator, setEditingEsgIndicator] = useState(null); // Track which ESG indicator is being edited
  
  // Temporary edit state to avoid focus loss
  const [tempSdgEdit, setTempSdgEdit] = useState({});
  const [tempEsgEdit, setTempEsgEdit] = useState({});
  
  // States for Inputs/Outputs/Outcomes editing
  const [showInputDialog, setShowInputDialog] = useState(false);
  const [showOutputDialog, setShowOutputDialog] = useState(false);
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [editingInputIndex, setEditingInputIndex] = useState(null);
  const [editingOutputIndex, setEditingOutputIndex] = useState(null);
  const [editingOutcomeIndex, setEditingOutcomeIndex] = useState(null);
  const [inputFormData, setInputFormData] = useState({ type: '', value: '', unit: '', comments: '' });
  const [outputFormData, setOutputFormData] = useState({ type: '', value: '', unit: '', comments: '' });
  const [outcomeFormData, setOutcomeFormData] = useState({ type: '', value: '', unit: '', comments: '' });

  useEffect(() => {
    fetchAllData();
  }, [projectId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, sroiRes, tocRes, sdgsRes, esgRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/impact-dashboard`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/projects/${projectId}/sroi-calculation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/projects/${projectId}/theory-of-change`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/projects/${projectId}/sdgs-alignment`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/projects/${projectId}/esg-indicators`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setDashboardData(dashboardRes.data);
      setSroiData(sroiRes.data);
      setTocData(tocRes.data);
      setSdgsData(sdgsRes.data);
      setEsgData(esgRes.data);
    } catch (error) {
      console.error('Failed to fetch impact data:', error);
      toast.error(t('sroi_calculator.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const updateSroiData = async (newSroiData) => {
    try {
      const response = await axios.post(`${API}/projects/${projectId}/sroi-calculation`, newSroiData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSroiData({...sroiData, ...newSroiData, sroi_ratio: response.data.sroi_ratio});
      setEditingSroi(false);
      toast.success('SROI calculation updated successfully');
      
      // Refresh dashboard
      fetchAllData();
    } catch (error) {
      console.error('Failed to update SROI:', error);
      toast.error('Failed to update SROI calculation');
    }
  };

  const updateSdgsData = async (newSdgsData) => {
    try {
      await axios.post(`${API}/projects/${projectId}/sdgs-alignment`, newSdgsData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSdgsData(newSdgsData);
      setEditingSdgs(false);
      toast.success('SDGs alignment updated successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to update SDGs:', error);
      toast.error('Failed to update SDGs alignment');
    }
  };

  const updateEsgData = async (newEsgData) => {
    try {
      await axios.post(`${API}/projects/${projectId}/esg-indicators`, newEsgData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setEsgData(newEsgData);
      setEditingEsg(false);
      toast.success('ESG indicators updated successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to update ESG:', error);
      toast.error('Failed to update ESG indicators');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getImpactColor = (score) => {
    if (score >= 80) return 'text-green-700';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-700';
    return 'text-red-600';
  };

  const getImpactBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  // Impact Dashboard Tab
  const ImpactDashboard = () => (
    <div className="space-y-6">
      {/* Overall Impact Score */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Award className="h-6 w-6 text-purple-600" />
              <span>Overall Impact Score</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchAllData}
              data-testid="refresh-impact-button"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className={`text-6xl font-bold ${getImpactColor(dashboardData.overall_impact_score)}`}>
              {dashboardData.overall_impact_score?.toFixed(1) || 0}
            </div>
            <p className="text-gray-600 mt-2">Out of 100 points</p>
            <div className={`inline-block px-4 py-2 rounded-full mt-4 ${getImpactBg(dashboardData.overall_impact_score)}`}>
              <span className={`font-semibold ${getImpactColor(dashboardData.overall_impact_score)}`}>
                {dashboardData.overall_impact_score >= 80 ? 'Exceptional Impact' :
                 dashboardData.overall_impact_score >= 60 ? 'Strong Impact' :
                 dashboardData.overall_impact_score >= 40 ? 'Moderate Impact' : 'Developing Impact'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Impact Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SROI Ratio</p>
                <p className="text-3xl font-bold text-green-700">
                  {dashboardData.summary?.sroi_ratio?.toFixed(2) || '0.00'}:1
                </p>
                <p className="text-xs text-gray-500">Social return per € invested</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-700 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SDGs Aligned</p>
                <p className="text-3xl font-bold text-blue-600">
                  {dashboardData.summary?.aligned_sdgs_count || 0}/17
                </p>
                <p className="text-xs text-gray-500">UN Sustainable Development Goals</p>
              </div>
              <Globe className="h-8 w-8 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">ESG Score</p>
                <p className="text-3xl font-bold text-purple-600">
                  {dashboardData.summary?.esg_score?.toFixed(1) || '0.0'}
                </p>
                <p className="text-xs text-gray-500">Environmental, Social & Governance</p>
              </div>
              <Shield className="h-8 w-8 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impact Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Impact Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-green-700 mb-3">Strengths</h4>
              <div className="space-y-2">
                {dashboardData.summary?.sroi_ratio >= 3 && (
                  <div className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-700" />
                    <span>Strong SROI ratio demonstrates value creation</span>
                  </div>
                )}
                {dashboardData.summary?.aligned_sdgs_count >= 3 && (
                  <div className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-700" />
                    <span>Significant SDG alignment across multiple goals</span>
                  </div>
                )}
                {dashboardData.summary?.esg_score >= 75 && (
                  <div className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-700" />
                    <span>Excellent ESG performance</span>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-blue-700 mb-3">Impact Areas</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm">
                  <Users className="h-4 w-4 text-blue-600" />
                  <span>Community empowerment and skills development</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Zap className="h-4 w-4 text-blue-600" />
                  <span>Economic growth and job creation</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Heart className="h-4 w-4 text-blue-600" />
                  <span>Social inclusion and equality</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Memoize handlers to prevent recreation
  const handlePresentValueChange = React.useCallback((e) => {
    setSroiData(prev => ({...prev, total_present_value: e.target.value}));
  }, []);

  const handleInvestmentChange = React.useCallback((e) => {
    setSroiData(prev => ({...prev, total_investment_new: e.target.value}));
  }, []);

  // Memoize the SROI Calculator Tab to prevent recreation
  const sroiCalculatorContent = React.useMemo(() => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calculator className="h-6 w-6 text-green-700" />
              <span>SROI Calculator</span>
            </div>
            {/* Only show edit button for PM/Admin */}
            {(user?.role === 'project_manager' || user?.role === 'administrator') && (
              <Button
                size="sm"
                variant={editingSroi ? "default" : "outline"}
                onClick={() => {
                  if (editingSroi) {
                    // Convert string values to numbers before saving
                    const dataToSave = {
                      ...sroiData,
                      total_present_value: parseFloat(sroiData.total_present_value) || 0,
                      total_investment_new: parseFloat(sroiData.total_investment_new) || 0
                    };
                    updateSroiData(dataToSave);
                  } else {
                    // Enter edit mode
                    setEditingSroi(true);
                  }
                }}
              >
                {editingSroi ? <Save className="h-4 w-4 mr-1" /> : <Edit className="h-4 w-4 mr-1" />}
                {editingSroi ? 'Save' : 'Edit'}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Total Present Value of Benefits (renamed from Total Investment) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Total Present Value of Benefits</label>
              {editingSroi ? (
                <input
                  type="number"
                  step="0.01"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={sroiData.total_present_value !== undefined ? sroiData.total_present_value : (sroiData.total_investment || '')}
                  onChange={handlePresentValueChange}
                />
              ) : (
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(sroiData.total_present_value || sroiData.total_investment)}</div>
              )}
            </div>

            {/* Total Investment (renamed from Total Social Value) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Total Investment</label>
              {editingSroi ? (
                <input
                  type="number"
                  step="0.01"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={sroiData.total_investment_new !== undefined ? sroiData.total_investment_new : (sroiData.total_social_value || '')}
                  onChange={handleInvestmentChange}
                />
              ) : (
                <div className="text-2xl font-bold text-green-700">{formatCurrency(sroiData.total_investment_new || sroiData.total_social_value)}</div>
              )}
            </div>

            {/* SROI Ratio - Updated formula: Total Present Value / Total Investment */}
            <div className="space-y-2">
              <label className="text-sm font-medium">SROI Ratio</label>
              <div className="text-3xl font-bold text-purple-600">
                {(() => {
                  const presentValue = parseFloat(sroiData.total_present_value || sroiData.total_investment || 0);
                  const investment = parseFloat(sroiData.total_investment_new || sroiData.total_social_value || 1);
                  const ratio = investment !== 0 ? (presentValue / investment) : 0;
                  return ratio.toFixed(2);
                })()}:1
              </div>
              <p className="text-xs text-gray-500">Return per dollar invested</p>
            </div>
          </div>

          {editingSroi && (
            <div className="flex space-x-2">
              <Button onClick={() => updateSroiData(sroiData)}>
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setEditingSroi(false)}>
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SROI Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Inputs & Outputs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Inputs & Outputs</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Inputs Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-blue-700">Inputs</h4>
                  {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setInputFormData({ type: '', value: '', unit: '', comments: '' });
                        setEditingInputIndex(null);
                        setShowInputDialog(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Input
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(sroiData.inputs || []).map((input, index) => (
                    <div key={index} className="text-sm p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-blue-900">{input.type}</div>
                          <div className="text-blue-700 font-semibold">
                            {input.value} {input.unit && <span className="text-xs">({input.unit})</span>}
                          </div>
                          {input.comments && <div className="text-gray-600 text-xs mt-1">{input.comments}</div>}
                        </div>
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setInputFormData(input);
                                setEditingInputIndex(index);
                                setShowInputDialog(true);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newInputs = sroiData.inputs.filter((_, i) => i !== index);
                                setSroiData({...sroiData, inputs: newInputs});
                                updateSroiData({...sroiData, inputs: newInputs});
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!sroiData.inputs || sroiData.inputs.length === 0) && (
                    <p className="text-sm text-gray-500 italic">No inputs added yet</p>
                  )}
                </div>
              </div>
              
              {/* Outputs Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-700">Outputs</h4>
                  {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setOutputFormData({ type: '', value: '', unit: '', comments: '' });
                        setEditingOutputIndex(null);
                        setShowOutputDialog(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Output
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(sroiData.outputs || []).map((output, index) => (
                    <div key={index} className="text-sm p-3 bg-green-50 rounded border border-green-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-green-900">{output.type}</div>
                          <div className="text-green-700 font-semibold">
                            {output.value} {output.unit && <span className="text-xs">({output.unit})</span>}
                          </div>
                          {output.comments && <div className="text-gray-600 text-xs mt-1">{output.comments}</div>}
                        </div>
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setOutputFormData(output);
                                setEditingOutputIndex(index);
                                setShowOutputDialog(true);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newOutputs = sroiData.outputs.filter((_, i) => i !== index);
                                setSroiData({...sroiData, outputs: newOutputs});
                                updateSroiData({...sroiData, outputs: newOutputs});
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!sroiData.outputs || sroiData.outputs.length === 0) && (
                    <p className="text-sm text-gray-500 italic">No outputs added yet</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Outcomes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Outcomes</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-purple-700">Outcomes</h4>
                {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setOutcomeFormData({ type: '', value: '', unit: '', comments: '' });
                      setEditingOutcomeIndex(null);
                      setShowOutcomeDialog(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Outcome
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(sroiData.outcomes || []).map((outcome, index) => (
                  <div key={index} className="text-sm p-3 bg-purple-50 rounded border border-purple-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-purple-900">{outcome.type}</div>
                        <div className="text-purple-700 font-semibold">
                          {outcome.value} {outcome.unit && <span className="text-xs">({outcome.unit})</span>}
                        </div>
                        {outcome.comments && <div className="text-gray-600 text-xs mt-1">{outcome.comments}</div>}
                      </div>
                      {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setOutcomeFormData(outcome);
                              setEditingOutcomeIndex(index);
                              setShowOutcomeDialog(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newOutcomes = sroiData.outcomes.filter((_, i) => i !== index);
                              setSroiData({...sroiData, outcomes: newOutcomes});
                              updateSroiData({...sroiData, outcomes: newOutcomes});
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {(!sroiData.outcomes || sroiData.outcomes.length === 0) && (
                  <p className="text-sm text-gray-500 italic">No outcomes added yet</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sensitivity Analysis */}
      {sroiData.sensitivity_analysis && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sensitivity Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(sroiData.sensitivity_analysis).map(([scenario, data]) => (
                <div key={scenario} className="text-center p-4 border rounded-lg">
                  <div className="text-lg font-semibold capitalize">{scenario}</div>
                  <div className="text-2xl font-bold text-purple-600">{data.sroi_ratio}:1</div>
                  <div className="text-sm text-gray-600">{data.description}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  ), [sroiData, editingSroi, user, handlePresentValueChange, handleInvestmentChange]);

  // SROI Calculator Tab component
  const SROICalculatorTab = () => sroiCalculatorContent;

  // SDGs Alignment Tab
  const SDGsAlignmentTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="h-6 w-6 text-blue-600" />
            <span>UN Sustainable Development Goals Alignment</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(sdgsData.goals || []).map((goal, index) => {
              const isEditing = editingSdgGoal === index;
              const editData = isEditing ? tempSdgEdit : goal;
              
              return (
              <Card key={goal.goal} className={`${goal.not_applicable ? 'bg-gray-100 border-gray-300' : goal.aligned ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-blue-600">SDG {goal.goal}</div>
                      <div className="text-sm font-medium">{goal.name}</div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {!goal.not_applicable && goal.aligned && !isEditing && (
                        <CheckCircle2 className="h-5 w-5 text-green-700" />
                      )}
                      {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                        isEditing ? (
                          <>
                            <Button 
                              size="sm" 
                              className="h-7 text-xs"
                              onClick={() => {
                                const newGoals = [...sdgsData.goals];
                                const baseline = parseFloat(tempSdgEdit.baseline) || 0;
                                const current = parseFloat(tempSdgEdit.current_value) || 0;
                                const target = parseFloat(tempSdgEdit.target) || 0;
                                const progress = target !== baseline ? Math.round(((current - baseline) / (target - baseline)) * 100) : 0;
                                
                                newGoals[index] = {
                                  ...tempSdgEdit,
                                  baseline,
                                  current_value: current,
                                  target,
                                  progress: Math.max(0, Math.min(100, progress))
                                };
                                
                                updateSdgsData({...sdgsData, goals: newGoals});
                                setEditingSdgGoal(null);
                                setTempSdgEdit({});
                              }}
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingSdgGoal(null);
                                setTempSdgEdit({});
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => {
                              setEditingSdgGoal(index);
                              setTempSdgEdit({...goal});
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                  
                  {isEditing && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`sdg-na-${goal.goal}`}
                          checked={editData.not_applicable || false}
                          onChange={(e) => {
                            setTempSdgEdit({
                              ...editData,
                              not_applicable: e.target.checked,
                              aligned: e.target.checked ? false : editData.aligned
                            });
                          }}
                          className="rounded"
                        />
                        <Label htmlFor={`sdg-na-${goal.goal}`} className="text-xs cursor-pointer">
                          Not Applicable
                        </Label>
                      </div>
                      
                      {!editData.not_applicable && (
                        <>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`sdg-aligned-${goal.goal}`}
                              checked={editData.aligned || false}
                              onChange={(e) => {
                                setTempSdgEdit({...editData, aligned: e.target.checked});
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`sdg-aligned-${goal.goal}`} className="text-xs cursor-pointer">
                              Aligned
                            </Label>
                          </div>
                          
                          {editData.aligned && (
                            <>
                              <div>
                                <Label className="text-xs">Baseline Value</Label>
                                <Input
                                  type="number"
                                  value={editData.baseline || ''}
                                  onChange={(e) => {
                                    setTempSdgEdit({...editData, baseline: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Baseline"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Current Value</Label>
                                <Input
                                  type="number"
                                  value={editData.current_value || ''}
                                  onChange={(e) => {
                                    setTempSdgEdit({...editData, current_value: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Current"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Target Value</Label>
                                <Input
                                  type="number"
                                  value={editData.target || ''}
                                  onChange={(e) => {
                                    setTempSdgEdit({...editData, target: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                  placeholder="Target"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Progress (Auto-calculated)</Label>
                                <div className="text-sm font-semibold text-blue-600 py-2">
                                  {(() => {
                                    const baseline = parseFloat(editData.baseline) || 0;
                                    const current = parseFloat(editData.current_value) || 0;
                                    const target = parseFloat(editData.target) || 0;
                                    const progress = target !== baseline ? Math.round(((current - baseline) / (target - baseline)) * 100) : 0;
                                    return Math.max(0, Math.min(100, progress));
                                  })()}%
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Comments</Label>
                                <Textarea
                                  value={editData.comments || ''}
                                  onChange={(e) => {
                                    setTempSdgEdit({...editData, comments: e.target.value});
                                  }}
                                  className="text-xs"
                                  rows={2}
                                  placeholder="Add comments..."
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {!isEditing && (
                    <>
                      {goal.not_applicable ? (
                        <div className="text-center py-4 text-gray-500 text-xs">
                          Not Applicable
                        </div>
                      ) : goal.aligned ? (
                        <div className="space-y-2">
                          <div className="text-xs text-gray-600">
                            <div className="flex justify-between">
                              <span>Baseline:</span>
                              <span className="font-semibold">{goal.baseline || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Current:</span>
                              <span className="font-semibold">{goal.current_value || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Target:</span>
                              <span className="font-semibold">{goal.target || 0}</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{width: `${goal.progress}%`}}
                            ></div>
                          </div>
                          <div className="text-xs text-right text-gray-600">
                            {goal.progress}% Progress
                          </div>
                          {goal.comments && (
                            <div className="text-xs text-gray-600 italic mt-2">
                              {goal.comments}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-xs">
                          Not aligned
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );})}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ESG Indicators Tab
  const ESGIndicatorsTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-6 w-6 text-purple-600" />
            <span>ESG Indicators Dashboard</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="environmental" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="environmental" className="flex items-center space-x-1">
                <Leaf className="h-4 w-4" />
                <span>Environmental</span>
              </TabsTrigger>
              <TabsTrigger value="social" className="flex items-center space-x-1">
                <Users className="h-4 w-4" />
                <span>Social</span>
              </TabsTrigger>
              <TabsTrigger value="governance" className="flex items-center space-x-1">
                <Building className="h-4 w-4" />
                <span>Governance</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="environmental" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {esgData.environmental && Object.entries(esgData.environmental).map(([key, indicator]) => {
                  const isEditing = editingEsgIndicator === `env-${key}`;
                  return (
                  <Card key={key} className={indicator.not_applicable ? 'bg-gray-100' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="flex items-center space-x-2">
                          {!indicator.not_applicable && !isEditing && (
                            <Badge className={indicator.trend === 'improving' ? 'bg-green-100 text-green-800' : 
                                            indicator.trend === 'stable' ? 'bg-blue-100 text-blue-800' : 
                                            'bg-yellow-100 text-yellow-800'}>
                              {indicator.trend}
                            </Badge>
                          )}
                          {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                            isEditing ? (
                              <>
                                <Button 
                                  size="sm" 
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    const newEnv = {...esgData.environmental};
                                    newEnv[key] = {
                                      ...tempEsgEdit,
                                      baseline: parseFloat(tempEsgEdit.baseline) || 0,
                                      value: parseFloat(tempEsgEdit.value) || 0,
                                      target: parseFloat(tempEsgEdit.target) || 0
                                    };
                                    updateEsgData({...esgData, environmental: newEnv});
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <Save className="h-3 w-3 mr-1" />
                                  Save
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => {
                                  setEditingEsgIndicator(`env-${key}`);
                                  setTempEsgEdit({...indicator});
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              id={`env-na-${key}`}
                              checked={tempEsgEdit.not_applicable || false}
                              onChange={(e) => {
                                setTempEsgEdit({
                                  ...tempEsgEdit,
                                  not_applicable: e.target.checked,
                                  aligned: e.target.checked ? false : tempEsgEdit.aligned
                                });
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`env-na-${key}`} className="text-xs cursor-pointer">
                              Not Applicable
                            </Label>
                          </div>
                          {!tempEsgEdit.not_applicable && (
                            <>
                              <div>
                                <Label className="text-xs">Baseline</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.baseline || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, baseline: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Current Value</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.value || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, value: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Target</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.target || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, target: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Comments</Label>
                                <Textarea
                                  value={tempEsgEdit.comments || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, comments: e.target.value});
                                  }}
                                  className="text-xs"
                                  rows={2}
                                  placeholder="Add comments..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {indicator.not_applicable ? (
                            <div className="text-center py-2 text-gray-500 text-xs">Not Applicable</div>
                          ) : (
                            <>
                              <div className="text-xs text-gray-600 space-y-1 mb-2">
                                <div className="flex justify-between">
                                  <span>Baseline:</span>
                                  <span className="font-semibold">{indicator.baseline || 0} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Current:</span>
                                  <span className="font-semibold">{indicator.value} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Target:</span>
                                  <span className="font-semibold">{indicator.target} {indicator.unit}</span>
                                </div>
                              </div>
                              {indicator.comments && (
                                <div className="text-xs text-gray-600 italic mt-2">
                                  {indicator.comments}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                );})}
              </div>
            </TabsContent>

            <TabsContent value="social" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {esgData.social && Object.entries(esgData.social).map(([key, indicator]) => {
                  const isEditing = editingEsgIndicator === `soc-${key}`;
                  return (
                  <Card key={key} className={indicator.not_applicable ? 'bg-gray-100' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="flex items-center space-x-2">
                          {!indicator.not_applicable && !isEditing && (
                            <Badge className={indicator.trend === 'improving' ? 'bg-green-100 text-green-800' : 
                                            indicator.trend === 'stable' ? 'bg-blue-100 text-blue-800' : 
                                            'bg-yellow-100 text-yellow-800'}>
                              {indicator.trend}
                            </Badge>
                          )}
                          {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                            isEditing ? (
                              <>
                                <Button 
                                  size="sm" 
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    const newSoc = {...esgData.social};
                                    newSoc[key] = {
                                      ...tempEsgEdit,
                                      baseline: parseFloat(tempEsgEdit.baseline) || 0,
                                      value: parseFloat(tempEsgEdit.value) || 0,
                                      target: parseFloat(tempEsgEdit.target) || 0
                                    };
                                    updateEsgData({...esgData, social: newSoc});
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <Save className="h-3 w-3 mr-1" />
                                  Save
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => {
                                  setEditingEsgIndicator(`soc-${key}`);
                                  setTempEsgEdit({...indicator});
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              id={`soc-na-${key}`}
                              checked={tempEsgEdit.not_applicable || false}
                              onChange={(e) => {
                                setTempEsgEdit({...tempEsgEdit, not_applicable: e.target.checked});
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`soc-na-${key}`} className="text-xs cursor-pointer">
                              Not Applicable
                            </Label>
                          </div>
                          {!tempEsgEdit.not_applicable && (
                            <>
                              <div>
                                <Label className="text-xs">Baseline</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.baseline || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, baseline: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Current Value</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.value || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, value: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Target</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.target || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, target: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Comments</Label>
                                <Textarea
                                  value={tempEsgEdit.comments || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, comments: e.target.value});
                                  }}
                                  className="text-xs"
                                  rows={2}
                                  placeholder="Add comments..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {indicator.not_applicable ? (
                            <div className="text-center py-2 text-gray-500 text-xs">Not Applicable</div>
                          ) : (
                            <>
                              <div className="text-xs text-gray-600 space-y-1 mb-2">
                                <div className="flex justify-between">
                                  <span>Baseline:</span>
                                  <span className="font-semibold">{indicator.baseline || 0} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Current:</span>
                                  <span className="font-semibold">{indicator.value} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Target:</span>
                                  <span className="font-semibold">{indicator.target} {indicator.unit}</span>
                                </div>
                              </div>
                              {indicator.comments && (
                                <div className="text-xs text-gray-600 italic mt-2">
                                  {indicator.comments}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                );})}
              </div>
            </TabsContent>

            <TabsContent value="governance" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {esgData.governance && Object.entries(esgData.governance).map(([key, indicator]) => {
                  const isEditing = editingEsgIndicator === `gov-${key}`;
                  return (
                  <Card key={key} className={indicator.not_applicable ? 'bg-gray-100' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="flex items-center space-x-2">
                          {!indicator.not_applicable && !isEditing && (
                            <Badge className={indicator.trend === 'improving' ? 'bg-green-100 text-green-800' : 
                                            indicator.trend === 'stable' ? 'bg-blue-100 text-blue-800' : 
                                            'bg-yellow-100 text-yellow-800'}>
                              {indicator.trend}
                            </Badge>
                          )}
                          {(user?.role === 'project_manager' || user?.role === 'administrator') && (
                            isEditing ? (
                              <>
                                <Button 
                                  size="sm" 
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    const newGov = {...esgData.governance};
                                    newGov[key] = {
                                      ...tempEsgEdit,
                                      baseline: parseFloat(tempEsgEdit.baseline) || 0,
                                      value: parseFloat(tempEsgEdit.value) || 0,
                                      target: parseFloat(tempEsgEdit.target) || 0
                                    };
                                    updateEsgData({...esgData, governance: newGov});
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <Save className="h-3 w-3 mr-1" />
                                  Save
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setEditingEsgIndicator(null);
                                    setTempEsgEdit({});
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => {
                                  setEditingEsgIndicator(`gov-${key}`);
                                  setTempEsgEdit({...indicator});
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              id={`gov-na-${key}`}
                              checked={tempEsgEdit.not_applicable || false}
                              onChange={(e) => {
                                setTempEsgEdit({...tempEsgEdit, not_applicable: e.target.checked});
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`gov-na-${key}`} className="text-xs cursor-pointer">
                              Not Applicable
                            </Label>
                          </div>
                          {!tempEsgEdit.not_applicable && (
                            <>
                              <div>
                                <Label className="text-xs">Baseline</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.baseline || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, baseline: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Current Value</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.value || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, value: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Target</Label>
                                <Input
                                  type="number"
                                  value={tempEsgEdit.target || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, target: e.target.value});
                                  }}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Comments</Label>
                                <Textarea
                                  value={tempEsgEdit.comments || ''}
                                  onChange={(e) => {
                                    setTempEsgEdit({...tempEsgEdit, comments: e.target.value});
                                  }}
                                  className="text-xs"
                                  rows={2}
                                  placeholder="Add comments..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {indicator.not_applicable ? (
                            <div className="text-center py-2 text-gray-500 text-xs">Not Applicable</div>
                          ) : (
                            <>
                              <div className="text-xs text-gray-600 space-y-1 mb-2">
                                <div className="flex justify-between">
                                  <span>Baseline:</span>
                                  <span className="font-semibold">{indicator.baseline || 0} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Current:</span>
                                  <span className="font-semibold">{indicator.value} {indicator.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Target:</span>
                                  <span className="font-semibold">{indicator.target} {indicator.unit}</span>
                                </div>
                              </div>
                              {indicator.comments && (
                                <div className="text-xs text-gray-600 italic mt-2">
                                  {indicator.comments}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                );})}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }


  // Handler functions for saving inputs/outputs/outcomes
  const handleSaveInput = () => {
    const newInputs = [...(sroiData.inputs || [])];
    if (editingInputIndex !== null) {
      newInputs[editingInputIndex] = inputFormData;
    } else {
      newInputs.push(inputFormData);
    }
    const updatedData = {...sroiData, inputs: newInputs};
    setSroiData(updatedData);
    updateSroiData(updatedData);
    setShowInputDialog(false);
  };

  const handleSaveOutput = () => {
    const newOutputs = [...(sroiData.outputs || [])];
    if (editingOutputIndex !== null) {
      newOutputs[editingOutputIndex] = outputFormData;
    } else {
      newOutputs.push(outputFormData);
    }
    const updatedData = {...sroiData, outputs: newOutputs};
    setSroiData(updatedData);
    updateSroiData(updatedData);
    setShowOutputDialog(false);
  };

  const handleSaveOutcome = () => {
    const newOutcomes = [...(sroiData.outcomes || [])];
    if (editingOutcomeIndex !== null) {
      newOutcomes[editingOutcomeIndex] = outcomeFormData;
    } else {
      newOutcomes.push(outcomeFormData);
    }
    const updatedData = {...sroiData, outcomes: newOutcomes};
    setSroiData(updatedData);
    updateSroiData(updatedData);
    setShowOutcomeDialog(false);
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Award className="h-6 w-6 text-purple-600" />
              <span>{t('sroi_calculator.header_title')}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchAllData}
              data-testid="refresh-sroi-button"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('sroi_calculator.refresh_all')}
            </Button>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" data-testid="impact-dashboard-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('sroi_calculator.tab_dashboard')}
          </TabsTrigger>
          <TabsTrigger value="sroi" data-testid="sroi-calculator-tab">
            <Calculator className="h-4 w-4 mr-2" />
            {t('sroi_calculator.tab_sroi')}
          </TabsTrigger>
          <TabsTrigger value="sdgs" data-testid="sdgs-alignment-tab">
            <Globe className="h-4 w-4 mr-2" />
            {t('sroi_calculator.tab_sdgs')}
          </TabsTrigger>
          <TabsTrigger value="esg" data-testid="esg-indicators-tab">
            <Shield className="h-4 w-4 mr-2" />
            {t('sroi_calculator.tab_esg')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <ImpactDashboard />
        </TabsContent>

        <TabsContent value="sroi">
          <SROICalculatorTab />
        </TabsContent>

        <TabsContent value="sdgs">
          <SDGsAlignmentTab />
        </TabsContent>

        <TabsContent value="esg">
          <ESGIndicatorsTab />
        </TabsContent>
      </Tabs>

      {/* Input Dialog */}
      <Dialog open={showInputDialog} onOpenChange={setShowInputDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingInputIndex !== null ? 'Edit Input' : 'Add Input'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={inputFormData.type}
                onChange={(e) => setInputFormData({...inputFormData, type: e.target.value})}
              >
                <option value="">Select type...</option>
                <option value="Funding">Funding</option>
                <option value="Volunteer Hours">Volunteer Hours</option>
                <option value="Materials">Materials</option>
                <option value="Training Sessions">Training Sessions</option>
                <option value="Equipment">Equipment</option>
                <option value="In-kind Donations">In-kind Donations</option>
                <option value="Staff Time">Staff Time</option>
                <option value="Facilities">Facilities</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                type="number"
                value={inputFormData.value}
                onChange={(e) => setInputFormData({...inputFormData, value: e.target.value})}
                placeholder="Enter value"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={inputFormData.unit}
                onChange={(e) => setInputFormData({...inputFormData, unit: e.target.value})}
              >
                <option value="">Select unit...</option>
                <option value="Number">Number</option>
                <option value="Euros (€)">Euros (€)</option>
                <option value="Percentage (%)">Percentage (%)</option>
                <option value="Hours">Hours</option>
                <option value="Days">Days</option>
                <option value="Units">Units</option>
                <option value="Kilograms (kg)">Kilograms (kg)</option>
                <option value="Meters (m)">Meters (m)</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Comments</Label>
              <Textarea
                value={inputFormData.comments}
                onChange={(e) => setInputFormData({...inputFormData, comments: e.target.value})}
                placeholder="Add comments..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowInputDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveInput}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Output Dialog */}
      <Dialog open={showOutputDialog} onOpenChange={setShowOutputDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingOutputIndex !== null ? 'Edit Output' : 'Add Output'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={outputFormData.type}
                onChange={(e) => setOutputFormData({...outputFormData, type: e.target.value})}
              >
                <option value="">Select type...</option>
                <option value="Number of People Trained">Number of People Trained</option>
                <option value="Workshops Delivered">Workshops Delivered</option>
                <option value="Services Delivered">Services Delivered</option>
                <option value="Events Held">Events Held</option>
                <option value="Materials Distributed">Materials Distributed</option>
                <option value="Products Created">Products Created</option>
                <option value="Programs Implemented">Programs Implemented</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                type="text"
                value={outputFormData.value}
                onChange={(e) => setOutputFormData({...outputFormData, value: e.target.value})}
                placeholder="Enter value"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={outputFormData.unit}
                onChange={(e) => setOutputFormData({...outputFormData, unit: e.target.value})}
              >
                <option value="">Select unit...</option>
                <option value="Number">Number</option>
                <option value="Percentage (%)">Percentage (%)</option>
                <option value="Sessions">Sessions</option>
                <option value="Events">Events</option>
                <option value="People">People</option>
                <option value="Units">Units</option>
                <option value="Hours">Hours</option>
                <option value="Days">Days</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Comments</Label>
              <Textarea
                value={outputFormData.comments}
                onChange={(e) => setOutputFormData({...outputFormData, comments: e.target.value})}
                placeholder="Add comments..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowOutputDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveOutput}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outcome Dialog */}
      <Dialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingOutcomeIndex !== null ? 'Edit Outcome' : 'Add Outcome'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={outcomeFormData.type}
                onChange={(e) => setOutcomeFormData({...outcomeFormData, type: e.target.value})}
              >
                <option value="">Select type...</option>
                <option value="Increased Employment">Increased Employment</option>
                <option value="Better Health">Better Health</option>
                <option value="Better Skills">Better Skills</option>
                <option value="Reduced Crime">Reduced Crime</option>
                <option value="Lives Improved">Lives Improved</option>
                <option value="Skills Gained">Skills Gained</option>
                <option value="Environmental Impact">Environmental Impact</option>
                <option value="Community Wellbeing">Community Wellbeing</option>
                <option value="Economic Growth">Economic Growth</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                type="number"
                value={outcomeFormData.value}
                onChange={(e) => setOutcomeFormData({...outcomeFormData, value: e.target.value})}
                placeholder="Enter value"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={outcomeFormData.unit}
                onChange={(e) => setOutcomeFormData({...outcomeFormData, unit: e.target.value})}
              >
                <option value="">Select unit...</option>
                <option value="Number">Number</option>
                <option value="Percentage (%)">Percentage (%)</option>
                <option value="Euros (€)">Euros (€)</option>
                <option value="People">People</option>
                <option value="Jobs">Jobs</option>
                <option value="Lives">Lives</option>
                <option value="Score">Score</option>
                <option value="Index">Index</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Comments</Label>
              <Textarea
                value={outcomeFormData.comments}
                onChange={(e) => setOutcomeFormData({...outcomeFormData, comments: e.target.value})}
                placeholder="Add comments..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowOutcomeDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveOutcome}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SROICalculator;